import { useId, type ChangeEvent, type ReactNode } from 'react';

/**
 * Textarea — TextField の多行版 (Sprint AS、docs/16 §10-2 P5 設計)。
 *
 * TextField とほぼ同じ規約 (label / value / onChange(value) / hint / error / id / a11y) に加え:
 *   - rows: 表示行数 (default 4)
 *   - maxLength: 文字数上限 (browser native, IME composing 中も適切に動く)
 *
 * onChange は `(value: string)` で文字列のみ渡す (TextField と統一)。
 */

export interface TextareaProps {
  label: ReactNode;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  required?: boolean;
  disabled?: boolean;
  error?: ReactNode;
  hint?: ReactNode;
  id?: string;
  className?: string;
}

const TEXTAREA_BASE =
  'block w-full rounded-md border bg-white text-slate-900 placeholder-slate-400 px-3 py-2 text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1 disabled:bg-slate-50 disabled:text-slate-500 disabled:cursor-not-allowed resize-y';
const TEXTAREA_NORMAL = 'border-slate-300 focus:border-primary-600 focus:ring-primary-400';
const TEXTAREA_ERROR = 'border-danger-600 focus:border-danger-600 focus:ring-danger-600';

export function Textarea({
  label,
  value,
  onChange,
  placeholder,
  rows = 4,
  maxLength,
  required,
  disabled,
  error,
  hint,
  id,
  className = '',
}: TextareaProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const hintId = hint ? `${inputId}-hint` : undefined;
  const errorId = error ? `${inputId}-error` : undefined;
  const describedBy = [errorId, hintId].filter(Boolean).join(' ') || undefined;
  const invalid = Boolean(error);

  return (
    <div className={className}>
      <label htmlFor={inputId} className="block text-sm font-medium text-slate-700 mb-1">
        {label}
        {required ? (
          <span aria-hidden="true" className="text-danger-600 ml-0.5">
            *
          </span>
        ) : null}
      </label>
      <textarea
        id={inputId}
        value={value}
        onChange={(e: ChangeEvent<HTMLTextAreaElement>) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        maxLength={maxLength}
        required={required}
        disabled={disabled}
        aria-invalid={invalid || undefined}
        aria-required={required || undefined}
        aria-describedby={describedBy}
        className={`${TEXTAREA_BASE} ${invalid ? TEXTAREA_ERROR : TEXTAREA_NORMAL}`}
      />
      {error ? (
        <p id={errorId} className="mt-1 text-xs text-danger-600">
          {error}
        </p>
      ) : null}
      {hint ? (
        <p id={hintId} className="mt-1 text-xs text-slate-600">
          {hint}
        </p>
      ) : null}
    </div>
  );
}
